import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  InputAdornment,
  Divider,
  LinearProgress,
  Collapse,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

// Mock data
import {
  mockOrganizationsList,
  mockMissingDocumentsSummary,
  mockMissingByType,
  mockOrganizationsWithMissing,
  getComplianceScoreColor,
  filterMissingDocuments,
  generateMissingDocumentsCsv,
  simulateDownload,
  simulateSendEmailReport,
  simulateDelay
} from '../../../mockData/orgDocIndex';

// MockBadge inline component
const MockBadge = () => (
  <Chip
    label="MOCK"
    size="small"
    color="warning"
    sx={{ ml: 1, fontSize: '10px', height: '20px' }}
  />
);

/**
 * Summary Tiles Component
 */
const SummaryTiles = ({ summary }) => {
  const tiles = [
    {
      label: 'Total Organizations',
      value: summary.totalOrganizations,
      color: '#1976d2',
      icon: <BusinessIcon />
    },
    {
      label: 'Compliant',
      value: summary.organizationsCompliant,
      color: '#4caf50',
      icon: <CheckCircleIcon />
    },
    {
      label: 'Non-Compliant',
      value: summary.organizationsWithMissing,
      color: '#f44336',
      icon: <CancelIcon />
    },
    {
      label: 'Compliance Rate',
      value: `${summary.complianceRate}%`,
      color: summary.complianceRate >= 80 ? '#4caf50' : summary.complianceRate >= 60 ? '#ff9800' : '#f44336',
      icon: <CheckCircleIcon />
    },
    {
      label: 'Total Missing',
      value: summary.totalMissingDocuments,
      color: '#ff5722',
      icon: <DescriptionIcon />
    },
    {
      label: 'Critical Missing',
      value: summary.criticalMissing,
      color: '#f44336',
      icon: <WarningIcon />
    }
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {tiles.map((tile, index) => (
        <Grid item xs={6} sm={4} md={2} key={index}>
          <Card
            sx={{
              bgcolor: tile.color,
              color: 'white',
              height: '100%'
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ mb: 1 }}>{tile.icon}</Box>
              <Typography variant="h4" fontWeight="bold">
                {tile.value}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {tile.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

/**
 * Missing by Type Chart
 */
const MissingByTypeChart = ({ data }) => {
  const maxCount = Math.max(...data.map(d => d.missingCount));

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Missing Documents by Type
        </Typography>
        <Box sx={{ mt: 2 }}>
          {data.map((item, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {item.typeName}
                  </Typography>
                  {item.isMandatory && (
                    <Chip label="Required" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
                <Typography variant="body2" fontWeight="bold" color={item.color}>
                  {item.missingCount}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(item.missingCount / maxCount) * 100}
                sx={{
                  height: 20,
                  borderRadius: 1,
                  bgcolor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: item.color
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {item.reason}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * Organization Row Component (Expandable)
 */
const OrganizationRow = ({ org }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {org.organisation.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {org.organisation.code}
          </Typography>
        </TableCell>
        <TableCell align="center">{org.totalRequired}</TableCell>
        <TableCell align="center">
          <Typography color="success.main" fontWeight="medium">
            {org.uploaded}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography color="error.main" fontWeight="bold">
            {org.missing}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={org.complianceScore}
              sx={{
                width: 80,
                height: 8,
                borderRadius: 1,
                bgcolor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: getComplianceScoreColor(org.complianceScore)
                }
              }}
            />
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ color: getComplianceScoreColor(org.complianceScore) }}
            >
              {org.complianceScore}%
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 4, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle2" gutterBottom>
                Missing Documents:
              </Typography>
              <List dense>
                {org.missingDocuments.map((doc, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {doc.isMandatory ? (
                        <CancelIcon color="error" fontSize="small" />
                      ) : (
                        <WarningIcon color="warning" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={doc.typeName}
                      secondary={doc.reason}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    {doc.isMandatory && (
                      <Chip label="Mandatory" size="small" color="error" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * Missing Documents Report Page
 */
const MissingDocumentsReport = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [byType, setByType] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    organizationId: 'ALL',
    documentType: 'ALL',
    organization: '',
    mandatoryOnly: false,
    sortBy: 'complianceScore'
  });

  // Organization options for dropdown
  const organizationOptions = [
    { value: 'ALL', label: 'All Organizations' },
    ...mockOrganizationsList.map(org => ({ value: org.id, label: `${org.name} (${org.code})` }))
  ];

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      await simulateDelay(500);
      setSummary(mockMissingDocumentsSummary);
      setByType(mockMissingByType);
      setOrganizations(mockOrganizationsWithMissing);
      setFilteredOrganizations(mockOrganizationsWithMissing);
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Apply filters
  useEffect(() => {
    const filtered = filterMissingDocuments(organizations, filters);
    setFilteredOrganizations(filtered);
    setPage(0);
  }, [filters, organizations]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    await simulateDelay(1000);
    const csv = generateMissingDocumentsCsv(filteredOrganizations);
    simulateDownload(csv, 'missing_documents_report.csv');
    setExporting(false);
    alert('[MOCK] CSV file downloaded: missing_documents_report.csv');
  };

  // Handle email
  const handleEmail = async () => {
    setEmailing(true);
    await simulateDelay(1000);
    simulateSendEmailReport('Missing Documents', ['admin@thaklis.ae']);
    setEmailing(false);
    alert('[MOCK] Report sent to admin@thaklis.ae');
  };

  // Handle filter change
  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  // Document type options
  const typeOptions = [
    { value: 'ALL', label: 'All Types' },
    ...byType.map(t => ({ value: t.typeCode, label: t.typeName }))
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Missing Documents Report
          </Typography>
          <MockBadge />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={emailing ? <CircularProgress size={16} /> : <EmailIcon />}
            onClick={handleEmail}
            disabled={emailing}
          >
            Email Report
          </Button>
        </Box>
      </Box>

      {/* Summary Tiles */}
      <SummaryTiles summary={summary} />

      {/* Chart */}
      <MissingByTypeChart data={byType} />

      {/* Organizations Table */}
      <Card>
        <CardContent>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              select
              label="Organization"
              value={filters.organizationId}
              onChange={handleFilterChange('organizationId')}
              size="small"
              sx={{ minWidth: 250 }}
            >
              {organizationOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Document Type"
              value={filters.documentType}
              onChange={handleFilterChange('documentType')}
              size="small"
              sx={{ minWidth: 200 }}
            >
              {typeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Search Organization"
              value={filters.organization}
              onChange={handleFilterChange('organization')}
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filters.mandatoryOnly}
                  onChange={(e) => setFilters(prev => ({ ...prev, mandatoryOnly: e.target.checked }))}
                  size="small"
                />
              }
              label="Mandatory Only"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>Organization</TableCell>
                  <TableCell align="center">Required</TableCell>
                  <TableCell align="center">Uploaded</TableCell>
                  <TableCell align="center">Missing</TableCell>
                  <TableCell>Compliance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrganizations
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((org) => (
                    <OrganizationRow key={org.id} org={org} />
                  ))}
                {filteredOrganizations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No organizations match the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredOrganizations.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default MissingDocumentsReport;
